<template>
    <div class="list-item">
        <div class="list-item__component">
            <div v-if="componentIcon" class="list-item__component__icon" >
                <Component :is="componentIcon" />
            </div>
            <div class="list-item__component__text">
                <div class="list-item__component__text-title">{{ notificationTitle }}</div>
                <div class="list-item__component__text-subtitle">{{ notificationDate }} · {{ notificationAction }}</div>
            </div>
        </div>
        <div
            class="list-item__action"
            @click="onNotificationActionClick"
        >
            <CornerDownRight />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ActivityTrackingListDto } from '@/api/generated/HCA'
import { computed } from 'vue'
import { TransformerFlatDto } from '@/api/queries/transformers/useTransformersHierarchyQuery'
import { useI18n } from 'vue-i18n'
import { format } from 'date-fns'
import { DATE_TIME_FORMAT } from '@/utils/date-utils'
import { StatusTypes } from '@/configuration/status/condition-status'
import { CornerDownRight } from 'lucide-vue-next'
import { useRouter } from 'vue-router'
import { CONDITION_STATUS_SCROLL_STATE_ACTION_TYPE_MAP } from '@/configuration/internal-redirect-states/condition-status-scroll-state'
import { ActionType } from '@/configuration/actions/action-type'
import { ComponentType, ComponentTypesMap } from '@/configuration/component/component-types'
import { GeneratorFlatDto } from '@/api/queries/generators/useGeneratorsHierarchyQuery'

const props = defineProps<{
    notification: ActivityTrackingListDto
    flattenedTransformersHierarchy: TransformerFlatDto[]
    flattenedGeneratorsHierarchy: GeneratorFlatDto[]
}>()

const { t } = useI18n()
const router = useRouter()

const componentIcon = computed(() => {
    if (!props.notification.componentType) {
        return
    }

    return ComponentTypesMap[props.notification.componentType as ComponentType]?.lucideIcon
})

const notificationAction = computed(() => {
    if (!props.notification.action) {
        return '-'
    }

    const previousStatus = StatusTypes.find((e) => e.value === props.notification.previousStatus)
    const newStatus = StatusTypes.find((e) => e.value === props.notification.newStatus)

    return t(`dashboard.notifications.actions.${props.notification.action}`, {
        previousStatus: previousStatus ? t(previousStatus.labelKey) : '-',
        newStatus: newStatus ? t(newStatus.labelKey) : '-',
    })
})

const notificationTitle = computed(() => {
    const transformerRecord = props.notification.componentType === ComponentType.Transformer ? props.flattenedTransformersHierarchy.find((e) => e.transformer.id === props.notification.componentSid) : undefined
    const generatorRecord = props.notification.componentType === ComponentType.Generator ? props.flattenedGeneratorsHierarchy.find((e) => e.generator.id === props.notification.componentSid) : undefined

    if (!transformerRecord && !generatorRecord) {
        return `${notificationAction.value}`
    }

    const componentType = props.notification.componentType === ComponentType.Transformer ? t('common.transformer') : t('common.generator')
    const plantGroup = transformerRecord?.plantGroup ?? generatorRecord?.plantGroup
    const plant = transformerRecord?.plant ?? generatorRecord?.plant
    const componentName = props.notification.componentName

    return `${componentType} / ${plantGroup?.name} / ${plant?.name} / ${componentName}`
})

const notificationDate = computed(() => format(new Date(props.notification.timestamp ?? new Date()), DATE_TIME_FORMAT))

const onNotificationActionClick = () => {
    if (props.notification.action as ActionType == ActionType.ThresholdEdited) {
        const route = router.resolve({
            name: 'Admin_Thresholds'
        })

        window.open(route.href, '_blank')
    }

    if (props.notification.componentType === ComponentType.Transformer) {
        const route = router.resolve({
            name: 'Transformers_ConditionStatus',
            params: {
                transformerId: props.notification.componentSid,
            },
            query: {
                focused: props.notification.action
                    ? CONDITION_STATUS_SCROLL_STATE_ACTION_TYPE_MAP[props.notification.action as ActionType]
                    : undefined,
            },
        })

        window.open(route.href, '_blank')
    }

    if (props.notification.componentType === ComponentType.Generator) {
        const route = router.resolve({
            name: 'Generators_ConditionStatus',
            params: {
                generatorId: props.notification.componentSid,
            },
            query: {
                focused: props.notification.action
                    ? CONDITION_STATUS_SCROLL_STATE_ACTION_TYPE_MAP[props.notification.action as ActionType]
                    : undefined,
            },
        })

        window.open(route.href, '_blank')
    }
}
</script>

<style scoped lang="scss">
.list-item {
    margin-right: $spacing-2xs;
    background-color: $pbl-background;
    color: $pbl-text;
    border-radius: 4px;

    display: flex;
    gap: $spacing-xs;
    align-items: center;
    justify-content: space-between;

    padding: $spacing-xs $spacing-s;

    &__component {
        display: flex;
        align-items: center;
        gap: $spacing-xs;

        &__icon {
            display: flex;
            align-items: center;
            justify-content: center;
            color: $pbl-text-muted;
        }

        &__text {
            display: flex;
            flex-direction: column;
            gap: $spacing-3xs;
    
            &-title {
                font-weight: 500;
            }
    
            &-subtitle {
                color: $pbl-text-muted;
            }
        }
    }

    &__action {
        cursor: pointer;

        color: $pbl-primary;

        &:hover:not(&:active) {
            color: $pbl-primary-contrast;
        }
    }
}
</style>
